import styled from '@emotion/styled';
import { Slider as AntdSlider } from 'antd';
import { SliderValue } from 'antd/lib/slider';
import { TextAlignProperty } from 'csstype';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { color, font } from 'src/shared/styles/variables';

const Wrapper = styled.div({
    marginTop: 100,
});

const QuestionText = styled.h4(
    {
        color: color.ebonyClay,
        marginTop: 15,
        marginBottom: 45,
        textAlign: 'center',
    },
    font.black(font.size.extraLarge),
);

const ShareFeedbackText = styled.h5(
    {
        color: color.baliHai,
        textAlign: 'center',
    },
    font.medium(font.size.mediumExtended),
);

const SliderWrapper = styled.div({
    width: '100%',
});

const Slider = styled(AntdSlider)(props => ({
    ':hover': {
        '.ant-slider-track': {
            backgroundColor: color.royalBlue,
        },
        '.ant-slider-handle:not(.ant-tooltip-open)': {
            borderColor: color.white,
        },
    },
    '&.ant-slider-with-marks': {
        paddingTop: 25,
    },
    '.ant-slider-rail': {
        height: 11,
        borderRadius: 8,
        backgroundColor: color.gallery,
    },
    '.ant-slider-track': {
        height: 11,
        borderRadius: 8,
        backgroundColor: color.royalBlue,
    },
    '.ant-slider-step': {
        height: 11,
    },
    '.ant-slider-dot': {
        display: 'none',
    },
    '.ant-slider-handle': {
        backgroundColor: color.royalBlue,
        width: '28px',
        height: '28px',
        border: `solid 6px ${color.white}`,
        marginTop: '-7px',
        boxShadow: '0px 1px 10px rgba(198, 198, 198, 0.5)',
    },
    '.ant-slider-mark': {
        top: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        [`& .ant-slider-mark-text:nth-of-type(${props.value})`]: {
            color: color.ebonyClay,
            ...font.heavy(font.size.almostExtraLarge),
        },
    },
    '.ant-slider-mark-text': {
        color: color.casper,
        transition: 'all 0.1s',
        ...font.heavy(font.size.mediumExtended),
    },
}));

const RatingLabelsList = styled.ul({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 0,
});

interface RatingLabelProps {
    textAlign?: TextAlignProperty;
}

const RatingLabel = styled.li<RatingLabelProps>(({ textAlign = 'start' }) => ({
    width: 80,
    color: color.steelGray,
    textAlign,
    ...font.medium(font.size.base),
}));

const TooEarlyText = styled.p({
    textAlign: 'center',
    padding: 5,
    cursor: 'pointer',
    marginTop: 170,
    marginBottom: 20,
    color: '#77869A',
    ...font.heavy(font.size.mediumExtended),
});

interface Props {
    question: string;
    selectedAnswer: number | null;
    onAnswerClick: (answer: null | SliderValue) => void;
}

export const RateSessionRangeQuestion: React.FC<Props> = ({
    question,
    selectedAnswer,
    onAnswerClick,
}) => {
    const { t } = useTranslation();

    return (
        <Wrapper>
            <ShareFeedbackText>
                {t('videoSessions:rate_session.range_question.title')}
            </ShareFeedbackText>
            <QuestionText>{question}</QuestionText>

            <SliderWrapper>
                <Slider
                    min={1}
                    max={5}
                    onChange={onAnswerClick}
                    value={selectedAnswer ?? -1}
                    step={1}
                    marks={{
                        1: '1',
                        2: '2',
                        3: '3',
                        4: '4',
                        5: '5',
                    }}
                    tipFormatter={null}
                    theme={{}}
                />
            </SliderWrapper>
            <RatingLabelsList>
                <RatingLabel>{t('videoSessions:rate_session.range_question.disagree')}</RatingLabel>
                <RatingLabel textAlign="center">
                    {t('videoSessions:rate_session.range_question.neutral')}
                </RatingLabel>
                <RatingLabel textAlign="end">
                    {t('videoSessions:rate_session.range_question.agree')}
                </RatingLabel>
            </RatingLabelsList>

            <TooEarlyText onClick={() => onAnswerClick(null)}>
                {t('videoSessions:rate_session.range_question.too_early')}
            </TooEarlyText>
        </Wrapper>
    );
};
